import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Wiring between the settings page and its subpages for the floating header
 * card. The page provides `setPageHeader` (title / subtitle / icon) and the DOM
 * node of the card's action slot, which `ConfigHeaderActions` portals into.
 */
export const ConfigHeaderContext = createContext({
    setPageHeader: () => {},
    actionsSlot: null,
});

/**
 * Publish this subpage's identity to the floating header card. Anything left
 * out falls back to the sidebar entry (its title and icon), and the header is
 * cleared when the subpage unmounts.
 *
 * `title` and `subtitle` must be strings: they are the effect's dependencies,
 * so a fresh element on every render would republish endlessly. The icon is
 * read through a ref for the same reason — it is static per page.
 *
 * @param {object}    header
 * @param {string}    [header.title]
 * @param {string}    [header.subtitle]
 * @param {ReactNode} [header.icon]
 */
export default function useConfigHeader({ title, subtitle, icon } = {}) {
    const { setPageHeader } = useContext(ConfigHeaderContext);

    const iconRef = useRef(icon);
    iconRef.current = icon;

    useEffect(() => {
        setPageHeader({ title, subtitle, icon: iconRef.current });
    }, [setPageHeader, title, subtitle]);

    useEffect(() => () => setPageHeader(null), [setPageHeader]);
}
